import { useEffect, useRef, useState } from "react";
import { Globe as GlobeIcon, ChevronDown as ChevronDownIcon } from "lucide-react";
import { useLanguage, type Language } from "@/hooks/useLanguage";
import styles from "./LanguageSelector.module.css";

interface LanguageOption {
  code: Language;
  name: string;
  flag: string;
}

// i18n-exempt — cada idioma é nomeado no próprio idioma
const LANGUAGES: LanguageOption[] = [
  { code: "pt", name: "Português", flag: "🇧🇷" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
];

export default function LanguageSelector() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const current =
    LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];

  useEffect(() => {
    if (!isOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className={styles.languageSelector}>
      <button
        type="button"
        className={styles.languageButton}
        onClick={() => setIsOpen((v) => !v)}
        title="Idioma"
      >
        <GlobeIcon size={20} />
        <span className={styles.currentLanguage}>{current.flag}</span>
        <ChevronDownIcon
          size={16}
          className={`${styles.chevron}${isOpen ? ` ${styles.chevronOpen}` : ""}`}
        />
      </button>

      {isOpen ? (
        <div className={styles.languageDropdown}>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => {
                void setLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`${styles.languageOption}${
                language === lang.code ? ` ${styles.languageOptionActive}` : ""
              }`}
            >
              <span className={styles.flag}>{lang.flag}</span>
              <span className={styles.name}>{lang.name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
